import { useRef, useState } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

// Reusable image upload with drag-and-drop + click-to-pick.
// props:
//   value: string  (current image URL — relative like `/api/files/...` or absolute)
//   onChange: (url: string) => void
//   label: string
//   className: string
export default function ImageUpload({ value, onChange, label = "Image", className = "" }) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please pick an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is larger than 8 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    upload(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    upload(f);
  };

  // Turn our relative /api/files URL into an absolute one for display
  const displaySrc = value
    ? value.startsWith("http")
      ? value
      : `${process.env.REACT_APP_BACKEND_URL || ""}${value}`
    : "";

  return (
    <div className={className}>
      {label && <div className="overline text-ink-soft mb-1.5">{label}</div>}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative border border-dashed cursor-pointer transition-colors ${
          dragging ? "bg-black/[0.05] border-ink" : "border-line hover:border-ink/50"
        } ${value ? "" : "aspect-[4/3]"} flex items-center justify-center overflow-hidden`}
        data-testid="image-upload-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFile}
          data-testid="image-upload-input"
        />

        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
            <Loader2 className="animate-spin" size={26} strokeWidth={1.4} />
          </div>
        )}

        {value ? (
          <div className="relative w-full">
            <img
              src={displaySrc}
              alt=""
              className="w-full h-56 object-cover bg-[#efece6]"
              data-testid="image-upload-preview"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              className="absolute top-2 right-2 bg-white/90 hover:bg-white text-ink px-2 py-1 text-[0.65rem] tracking-[0.22em] uppercase flex items-center gap-1 shadow-sm"
              data-testid="image-upload-remove"
            >
              <X size={11} /> Remove
            </button>
          </div>
        ) : (
          <div className="text-center px-4 py-8">
            <div className="mx-auto w-10 h-10 rounded-full bg-black/[0.04] flex items-center justify-center mb-3">
              {dragging ? <Upload size={16} /> : <ImageIcon size={16} strokeWidth={1.3} />}
            </div>
            <p className="text-sm text-ink">
              {dragging ? "Drop the image" : "Click or drag an image here"}
            </p>
            <p className="text-[0.65rem] tracking-[0.22em] uppercase text-ink-soft mt-1.5">
              JPG · PNG · WEBP · up to 8 MB
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
